import { Box, Button, Card, Container, Grid, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import ListSkeleton from 'src/components/list/list-skeleton';
import { useTable } from 'src/components/table';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTranslate } from 'src/locales';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';
import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import { PageOptions, PaginatedResponse } from 'src/services/api.types';
import { useLazySearchOrganizationCandidatesQuery } from 'src/services/candidates/candidates.service';
import { IOrgCandidate } from 'src/services/candidates/candidates.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import CandidateSearchFilters from '../components/candidate-search-filters';
import CandidateSearchResults from '../components/candidate-search-results';
import { SearchFormValues } from '../types/search.types';
import { buildSearchDSL } from '../utils/search.utils';

const DEFAULT_PAGE_OPTIONS: PageOptions = {
  page: 1,
  per_page: 10,
};

type Props = {
  jobId?: string;
}

export default function CandidateSearchView(
  { jobId }: Props
) {

  const { t } = useTranslate();
  const resultsTable = useTable();
  const tenantType = useOrgTenant();

  const actionsControl = usePopover();

  const bulkAddCandidateJobControl = useBoolean();

  const addToListControl = useBoolean();

  // State for search form values - initialize with advanced mode enabled
  const [searchValues, setSearchValues] = useState<SearchFormValues>({
    search_dsl: { op: 'AND', children: [] }
  });

  // State for pagination
  const [pageOptions, setPageOptions] = useState<PageOptions>(DEFAULT_PAGE_OPTIONS);
  const [candidatesData, setCandidatesData] = useState<PaginatedResponse<IOrgCandidate>>({
    results: [],
    count: 0,
  });

  const [searchCandidates, { isLoading, isFetching }] = useLazySearchOrganizationCandidatesQuery();

  // Handle search form submission
  const handleSearch = useCallback(async (values: SearchFormValues, pageData: PageOptions) => {

    try {
      const response = await searchCandidates({
        search_dsl: buildSearchDSL(values),
        page: pageData,
      }).unwrap();

      setCandidatesData(response);

    } catch (error) {
      console.error('Failed to search candidates:', error);
    }
  }, [searchCandidates]);

  const onSearch = useCallback(async (values: SearchFormValues) => {
    setSearchValues(values);
    setPageOptions(DEFAULT_PAGE_OPTIONS); // reset to first page on new search

    handleSearch(values, DEFAULT_PAGE_OPTIONS);
  }, [handleSearch]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setSearchValues({});
    setPageOptions(DEFAULT_PAGE_OPTIONS);

    handleSearch({}, DEFAULT_PAGE_OPTIONS);
  }, [handleSearch]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page,
    }));

    handleSearch(searchValues, {
      ...pageOptions,
      page,
    });
  }, [handleSearch, searchValues, pageOptions]);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    setPageOptions((prev) => ({
      ...prev,
      per_page: rowsPerPage,
      page: 1, // Reset to first page when changing rows per page
    }));

    handleSearch(searchValues, {
      ...pageOptions,
      per_page: rowsPerPage,
      page: 1,
    })
  }, [handleSearch, searchValues, pageOptions]);

  // Loading state
  const isSearching = isLoading || isFetching;

  // Empty state
  const isEmpty = !isSearching && (!candidatesData || candidatesData.results.length === 0);

  // Initial state (no search performed yet)
  const isInitial = Object.keys(searchValues).length === 0 && !candidatesData;

  const handleAddCandidatesToJob = useCallback((candidateIds: string[]) => {
    bulkAddCandidateJobControl.onTrue();
  }, [bulkAddCandidateJobControl]);

  const handleAddCandidatesToList = useCallback((candidateIds: string[]) => {
    addToListControl.onTrue();
  }, [addToListControl]);

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3}>
        {/* Search Filters - 1 part of the 1:4 ratio */}
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3, height: '100%' }}>
            <CandidateSearchFilters
              onSearch={onSearch}
              onClearFilters={handleClearFilters}
              initialValues={searchValues}
            />
          </Card>
        </Grid>

        {/* Search Results - 4 parts of the 1:4 ratio */}
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 3 }}>
            {isSearching && (
              <Stack>
                <ListSkeleton height={60} />
              </Stack>
            )}

            {isInitial && (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body1">
                  {t('candidates.search.initial_message')}
                </Typography>
              </Box>
            )}

            {isEmpty && (
              <EmptyContent
                title={t('candidates.search.no_results')}
                description={t('candidates.search.try_different_filters')}
                sx={{ py: 5 }}
              />
            )}

            {!isSearching && candidatesData && candidatesData.results.length > 0 && (
              <Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Stack >
                    <Typography variant="h6">
                      {t('candidates.search.results', { count: candidatesData.count })}
                    </Typography>
                    {
                      !!resultsTable.selected.length &&
                      <Typography variant="caption">
                        {t('candidates.search.selected', { count: resultsTable.selected.length })}
                      </Typography>
                    }
                  </Stack>

                  <Button
                    size="medium"
                    onClick={actionsControl.onOpen}
                    startIcon={<Iconify icon="solar:hamburger-menu-line-duotone" />}
                    variant="outlined"
                    sx={{ textWrap: 'nowrap' }}
                  >
                    {t('common.actions')}
                  </Button>
                </Stack>

                <CandidateSearchResults
                  table={resultsTable}
                  candidates={candidatesData.results}
                  total={candidatesData.count}
                  page={pageOptions.page}
                  rowsPerPage={pageOptions.per_page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </Stack>

            )}
          </Card>
        </Grid>
      </Grid>

      <CustomPopover
        open={actionsControl.open}
        onClose={actionsControl.onClose}
        arrow="top-right"
      >

        <Tooltip title="">
          <span>
            <MenuItem
              disabled={!resultsTable?.selected.length}
              onClick={() => {
                actionsControl.onClose();
                handleAddCandidatesToJob(resultsTable?.selected ?? []);
              }}
            >
              <Iconify icon="mdi:invite" />
              {t('candidates.add_bulk')}
            </MenuItem>
          </span>
        </Tooltip>

        <Tooltip title={!resultsTable?.selected.length ? t('lists.bulk_add_disabled_tootip') : ''}>
          <span>
            <MenuItem
              disabled={!resultsTable?.selected.length}
              onClick={() => {
                actionsControl.onClose();
                handleAddCandidatesToList(resultsTable?.selected ?? []);
              }}
            >
              <Iconify icon="material-symbols:contextual-token-add-outline" />
              {t('lists.add_to_list')}
            </MenuItem>
          </span>
        </Tooltip>

      </CustomPopover>

      <BulkAddCandidatesJobModal open={bulkAddCandidateJobControl.value} onClose={bulkAddCandidateJobControl.onFalse} candidates={resultsTable.selected} jobId={jobId} />

      <AddItemsModal
        open={addToListControl.value}
        onClose={addToListControl.onFalse}
        items={resultsTable.selected}
        entityType={ListEntityType.CANDIDATE}
      />

    </Container>
  );
}
